import { TrendingUp, Package, Star } from 'lucide-react';

export function TopProductsSection({ data }) {
  // Process the product data
  const processProducts = (inputData) => {
    if (!inputData) return [];

    // Case 1: If data is already in the expected format
    if (Array.isArray(inputData) && inputData.length > 0) {
      // Check if it's already formatted correctly
      if (inputData[0].product !== undefined) {
        return inputData;
      }

      // Case 2: If data is from Table2 (API response)
      if (inputData[0].Product !== undefined) {
        return inputData.map(item => ({
          product: item.Product || 'Unknown Product',
          category: item.CategoryName || 'Uncategorized',
          quantity: item.Quantity || 0,
          sales: item.SalesAmount || 0,
          percentage: item.Percentage || 0
        }));
      }

      // Case 3: Generic mapping
      return inputData.map(item => ({
        product: item.product || item.name || item.Product || 'Unknown',
        category: item.category || item.CategoryName || 'General',
        quantity: item.quantity || item.Quantity || item.qty || 0,
        sales: item.sales || item.SalesAmount || item.amount || item.Amount || 0,
        percentage: item.percentage || item.Percentage || 0
      }));
    }

    // Case 4: If data is an object with products property
    if (inputData.products && Array.isArray(inputData.products)) {
      return inputData.products.map(item => ({
        product: item.product || item.name || item.Product || 'Unknown',
        category: item.category || item.CategoryName || 'General',
        quantity: item.quantity || item.Quantity || 0,
        sales: item.sales || item.SalesAmount || 0,
        percentage: item.percentage || item.Percentage || 0
      }));
    }

    return [];
  };

  const processedData = processProducts(data);
  
  // Sort by sales amount (highest first)
  const sortedData = [...processedData].sort((a, b) => b.sales - a.sales);
  
  // Calculate total sales for percentage calculation if not provided
  const totalSales = sortedData.reduce((sum, item) => sum + (item.sales || 0), 0);
  
  // Ensure each item has a percentage
  const productsWithPercentage = sortedData.map(item => ({
    ...item,
    percentage: item.percentage || (totalSales > 0 ? (item.sales / totalSales) * 100 : 0)
  }));

  // If no data after processing, show empty state
  if (productsWithPercentage.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Top Products</h3>
              <p className="text-sm text-slate-500">Best selling items</p>
            </div>
          </div>
        </div>
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No product data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Top Products</h3>
            <p className="text-sm text-slate-500">Best selling items</p>
          </div>
        </div>
        
        {/* Summary stats */}
        <div className="text-right">
          <p className="text-xs text-slate-500">Total Products</p>
          <p className="text-lg font-bold text-slate-800">{productsWithPercentage.length}</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[650px] overflow-y-auto pr-2 custom-scrollbar">
        {productsWithPercentage.slice(0, 10).map((product, index) => {
          // Clean product name (remove trailing dashes)
          const cleanName = product.product
            .replace(/\s+-\s+-\s+-\s+-.*$/, '')
            .replace(/\s+-\s+-\s+-\s*$/, '')
            .trim();
          
          return (
            <div
              key={index}
              className="group p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl hover:shadow-md transition-all border border-slate-200 hover:border-amber-200"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-800 truncate group-hover:text-[#01796d] transition-colors">
                        {cleanName || 'Unknown Product'}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {product.category || 'Uncategorized'}
                        </p>
                        {index === 0 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Best Seller
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-[#01796d]">
                        Rs. {(product.sales || 0).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Qty: {(product.quantity || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                      <span>Sales Share</span>
                      <span className="font-medium text-amber-600">
                        {(product.percentage || 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-br from-amber-500 to-orange-600 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(product.percentage || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {productsWithPercentage.length > 10 && (
        <div className="mt-4 pt-4 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-500">
            Showing top 10 of {productsWithPercentage.length} products
          </p>
        </div>
      )}

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}